import { FC, JSX, useEffect, useMemo, useState } from "react";
import { ColumnConfig, WafflesThemes, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles/Waffles";
import PageSection from "@app/shared/PageSection";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ProviderHubContractEntityProviders } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import { NO_ACTIONS_APPLIED_LABEL, formatCountiesImpacted, formatGapImpactCount, formatScore, formatSpecialtyWithCode, shouldShowGapImpactBreakdown } from "@app/projects/network-assessment/provider-hub/utility";

// AC 193447: "All providers should roll up to an Organization within the CE." Any type-1 practitioner that arrives
// without a primary organization is bucketed here rather than dropped, so row counts stay consistent across views.
const OTHER_PRACTITIONERS_KEY = "other-practitioners";
const OTHER_PRACTITIONERS_LABEL = "Other Practitioners";

interface PractitionerTableRow {
  [wafflesKeyColumnName]: string;
  name: JSX.Element | string;
  identifier: string;
  providerSource: JSX.Element | string;
  status: JSX.Element | string;
  affiliationStrength: string | number;
  gapImpact: string | number;
  specialtiesImpacted: JSX.Element | string;
  activeGapImpact: string | number;
  pendingGapImpact: string | number;
  approvedGapImpact: string | number;
  opportunityGapImpact: string | number;
  countiesImpacted: JSX.Element | string;
  accessPoints: string | number;
  effectiveness: string | number;
  efficiency: string | number;
  totalClaims: string | number;
  medicareClaims: string | number;
  medicaidClaims: string | number;
  commercialClaims: string | number;
  vaClaims: string | number;
  tricareClaims: string | number;
  motiveAppropriateness: string | number;
  careJourneyCost: string | number;
  careJourneyQuality: string | number;
}

interface ContractEntityEmployedPractitionersTableProps {
  // Flat list of type-1 practitioner records for the contract entity; grouped by primary organization NPI2 here.
  // (Interim: the backend parent-paginated + lazy-children endpoint replaces this in a later slice.)
  practitioners: ProviderHubContractEntityProviders[];
  showPci: boolean;
  showVaClaims: boolean;
  showTricareClaims: boolean;
  showMotive: boolean;
  showCareJourney: boolean;
  // Navigates to the Organization profile for the given NPI2 + contract entity (same tab). Wired by the CE profile.
  onOrganizationClick: (organizationNpi: string, contractEntityId?: number) => void;
  // Navigates to the Provider profile for a practitioner (same tab). Wired by the CE profile.
  onProviderClick: (provider: ProviderHubContractEntityProviders) => void;
  // While true, Waffles shows a loading spinner instead of the empty-table message.
  loading?: boolean;
}

const networkTypeBadges = (networkTypes: ReadonlyArray<ProviderHubContractEntityProviders["providerNetworkTypes"][number]>): JSX.Element => (
  <>
    {sortProviderNetworkTypes([...networkTypes]).map((networkType) => (
      <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
    ))}
  </>
);

const actionStatusBadges = (statuses: ReadonlyArray<ProviderHubContractEntityProviders["providerActionStatuses"][number]>): JSX.Element => (
  statuses.length > 0
    ? <>{statuses.map((status, index) => <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />)}</>
    : <>{NO_ACTIONS_APPLIED_LABEL}</>
);

const providerSourceCell = (p: ProviderHubContractEntityProviders): JSX.Element => networkTypeBadges(p.providerNetworkTypes ?? []);

const statusCell = (p: ProviderHubContractEntityProviders): JSX.Element => actionStatusBadges(p.providerActionStatuses ?? []);

// Distinct union of a field across a group's practitioners, for the roll-up values shown on the organization (parent) row.
const distinctUnion = <T,>(children: ProviderHubContractEntityProviders[], select: (p: ProviderHubContractEntityProviders) => T[]): T[] =>
  Array.from(new Set(children.flatMap((p) => select(p) ?? [])));

// Provider grain: each practitioner counts once regardless of how many access points it has. Used for Effectiveness
// and Efficiency.
const averageByProvider = (children: ProviderHubContractEntityProviders[], select: (p: ProviderHubContractEntityProviders) => number | undefined | null): number | undefined => {
  const values = children.map(select).filter((v): v is number => v != null);
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : undefined;
};

// Access-point grain: a practitioner's score is the mean of its own access points, so weighting by access-point count
// gives the mean across the organization's access points. Used for Motive and CareJourney.
const averageByAccessPoint = (children: ProviderHubContractEntityProviders[], select: (p: ProviderHubContractEntityProviders) => number | undefined | null): number | undefined => {
  let weightedTotal = 0;
  let accessPoints = 0;

  for (const child of children) {
    const value = select(child);
    const childAccessPoints = child.accessPointCount ?? 0;
    if (value == null || childAccessPoints <= 0) continue;

    weightedTotal += value * childAccessPoints;
    accessPoints += childAccessPoints;
  }

  return accessPoints > 0 ? weightedTotal / accessPoints : undefined;
};

const ContractEntityEmployedPractitionersTable: FC<ContractEntityEmployedPractitionersTableProps> = (props) => {
  const useRegionStatusBreakdown = useMemo<boolean>(
    () => shouldShowGapImpactBreakdown(props.practitioners),
    [props.practitioners]
  );

  /* oxlint-disable sort-keys */
  const columnConfig: ColumnConfig = {
    name: { displayText: "Name", sizeContentToFit: true, sticky: true, testId: "ce-practitioners-table-name", noSort: true },
    identifier: { displayText: "Identifier", sizeContentToFit: true, sticky: true, testId: "ce-practitioners-table-identifier", noSort: true },
    providerSource: { displayText: "Provider Source", sizeContentToFit: true, testId: "ce-practitioners-table-provider-source", tooltip: "Indicates the original database from which the provider data was obtained", noSort: true },
    status: { displayText: "Status", sizeContentToFit: true, testId: "ce-practitioners-table-status", tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a provider", noSort: true },
    affiliationStrength: { displayText: "Affiliation Strength", sizeContentToFit: true, testId: "ce-practitioners-table-affiliation-strength", tooltip: "Indicates the strength of the affiliation", noSort: true },
    gapImpact: { displayText: "Gap Impact", sizeContentToFit: true, testId: "ce-practitioners-table-gap-impact", tooltip: "Indicates the distinct number of gaps that the provider closes", noSort: true, hidden: useRegionStatusBreakdown },
    specialtiesImpacted: { displayText: "Specialty Groups Impacted", sizeContentToFit: true, testId: "ce-practitioners-table-specialty", noSort: true },
    activeGapImpact: { displayText: "Active Gap Impact", sizeContentToFit: true, testId: "ce-practitioners-table-active-gap-impact", tooltip: "Count of gaps closeable in Active service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    pendingGapImpact: { displayText: "Pending Gap Impact", sizeContentToFit: true, testId: "ce-practitioners-table-pending-gap-impact", tooltip: "Count of gaps closeable in Pending service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    approvedGapImpact: { displayText: "Approved Gap Impact", sizeContentToFit: true, testId: "ce-practitioners-table-approved-gap-impact", tooltip: "Count of gaps closeable in Approved service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    opportunityGapImpact: { displayText: "Opportunity Gap Impact", sizeContentToFit: true, testId: "ce-practitioners-table-opportunity-gap-impact", tooltip: "Count of gaps closeable in Opportunity-to-expand service area counties", noSort: true, hidden: !useRegionStatusBreakdown },
    countiesImpacted: { displayText: "Counties Impacted", sizeContentToFit: true, testId: "ce-practitioners-table-county", noSort: true },
    accessPoints: { displayText: "Access Points", sizeContentToFit: true, testId: "ce-practitioners-table-access-points", noSort: true },
    effectiveness: { displayText: "Effectiveness (Quality)", sizeContentToFit: true, testId: "ce-practitioners-table-effectiveness", hidden: !props.showPci, noSort: true },
    efficiency: { displayText: "Efficiency (Cost)", sizeContentToFit: true, testId: "ce-practitioners-table-efficiency", hidden: !props.showPci, noSort: true },
    totalClaims: { displayText: "Total Claims", sizeContentToFit: true, testId: "ce-practitioners-table-total-claims", hidden: !props.showPci, noSort: true },
    medicareClaims: { displayText: "Medicare Claims", sizeContentToFit: true, testId: "ce-practitioners-table-medicare-claims", hidden: !props.showPci, noSort: true },
    medicaidClaims: { displayText: "Medicaid Claims", sizeContentToFit: true, testId: "ce-practitioners-table-medicaid-claims", hidden: !props.showPci, noSort: true },
    commercialClaims: { displayText: "Commercial Claims", sizeContentToFit: true, testId: "ce-practitioners-table-commercial-claims", hidden: !props.showPci, noSort: true },
    vaClaims: { displayText: "VA Claims", sizeContentToFit: true, testId: "ce-practitioners-table-va-claims", hidden: !props.showVaClaims, noSort: true },
    tricareClaims: { displayText: "TRICARE Claims", sizeContentToFit: true, testId: "ce-practitioners-table-tricare-claims", hidden: !props.showTricareClaims, noSort: true },
    motiveAppropriateness: { displayElement: <ProviderScoreColumnHeader variant="motive" />, sizeContentToFit: true, testId: "ce-practitioners-table-motive", hidden: !props.showMotive, noSort: true },
    careJourneyCost: { displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />, sizeContentToFit: true, testId: "ce-practitioners-table-carejourney-cost", hidden: !props.showCareJourney, noSort: true },
    careJourneyQuality: { displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />, sizeContentToFit: true, testId: "ce-practitioners-table-carejourney-quality", hidden: !props.showCareJourney, noSort: true },
  };
  /* oxlint-enable sort-keys */

  const groups = useMemo(() => {
    const byOrg = new Map<string, ProviderHubContractEntityProviders[]>();
    const other: ProviderHubContractEntityProviders[] = [];
    for (const practitioner of props.practitioners) {
      if (practitioner.professionalOrganizationNpi) {
        const bucket = byOrg.get(practitioner.professionalOrganizationNpi) ?? [];
        bucket.push(practitioner);
        byOrg.set(practitioner.professionalOrganizationNpi, bucket);
      } else {
        other.push(practitioner);
      }
    }
    return { byOrg, other };
  }, [props.practitioners]);

  /* oxlint-disable sort-keys */
  const childRow = (p: ProviderHubContractEntityProviders, parentKey: string): PractitionerTableRow => ({
    [wafflesKeyColumnName]: `${parentKey}:${p.npi}`,
    name: p.name
      ? <QESButton qesType="link" qesTestId={`ce-practitioners__provider-link--${p.npi}`} className="demibold" onClick={() => props.onProviderClick(p)}>{p.name}</QESButton>
      : "---",
    identifier: `NPI (Type 1): ${p.npi}`,
    providerSource: providerSourceCell(p),
    status: statusCell(p),
    affiliationStrength: p.affiliationStrength != null ? `${Math.round(p.affiliationStrength * 100)}%` : "---",
    gapImpact: formatGapImpactCount(p.gapsClosed),
    specialtiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={(p.specialtiesImpacted ?? []).map(formatSpecialtyWithCode)} numberOfElementsPerRow={3} />,
    activeGapImpact: formatGapImpactCount(p.activeGapsClosed),
    pendingGapImpact: formatGapImpactCount(p.pendingGapsClosed),
    approvedGapImpact: formatGapImpactCount(p.approvedGapsClosed),
    opportunityGapImpact: formatGapImpactCount(p.opportunityGapsClosed),
    countiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={formatCountiesImpacted(p.countiesImpacted)} numberOfElementsPerRow={3} />,
    accessPoints: p.accessPointCount ?? "---",
    effectiveness: formatScore(p.effectivenessRank),
    efficiency: formatScore(p.efficiencyRank),
    totalClaims: p.totalClaimVolume ?? "---",
    medicareClaims: p.medicareClaimVolume ?? "---",
    medicaidClaims: p.medicaidClaimVolume ?? "---",
    commercialClaims: p.commercialClaimVolume ?? "---",
    vaClaims: p.vaClaimVolume ?? "---",
    tricareClaims: p.tricareClaimVolume ?? "---",
    motiveAppropriateness: formatScore(p.motiveAppropriateness),
    careJourneyCost: formatScore(p.careJourneyCost),
    careJourneyQuality: formatScore(p.careJourneyQuality),
  });

  // The organization (parent) row rolls up its practitioners: distinct union of specialties / provider sources / statuses /
  // counties, and summed gap impact + access points (mirrors the aggregate org row in the mockup).
  const parentRow = (key: string, name: JSX.Element | string, identifier: string, children: ProviderHubContractEntityProviders[]): PractitionerTableRow => ({
    [wafflesKeyColumnName]: key,
    name,
    identifier,
    providerSource: networkTypeBadges(distinctUnion(children, (p) => p.providerNetworkTypes ?? [])),
    status: actionStatusBadges(distinctUnion(children, (p) => p.providerActionStatuses ?? [])),
    affiliationStrength: "---",
    gapImpact: formatGapImpactCount(children.reduce((sum, p) => sum + (p.gapsClosed ?? 0), 0)),
    specialtiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={distinctUnion(children, (p) => (p.specialtiesImpacted ?? []).map(formatSpecialtyWithCode))} numberOfElementsPerRow={3} />,
    activeGapImpact: formatGapImpactCount(children.reduce((sum, p) => sum + (p.activeGapsClosed ?? 0), 0)),
    pendingGapImpact: formatGapImpactCount(children.reduce((sum, p) => sum + (p.pendingGapsClosed ?? 0), 0)),
    approvedGapImpact: formatGapImpactCount(children.reduce((sum, p) => sum + (p.approvedGapsClosed ?? 0), 0)),
    opportunityGapImpact: formatGapImpactCount(children.reduce((sum, p) => sum + (p.opportunityGapsClosed ?? 0), 0)),
    countiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={formatCountiesImpacted(children.flatMap((p) => p.countiesImpacted ?? []))} numberOfElementsPerRow={3} />,
    accessPoints: children.reduce((sum, p) => sum + (p.accessPointCount ?? 0), 0),
    effectiveness: formatScore(averageByProvider(children, (p) => p.effectivenessRank)),
    efficiency: formatScore(averageByProvider(children, (p) => p.efficiencyRank)),
    // Claims volume is a per-provider bucket that doesn't roll up, so it's blank on the aggregate org row and shown on child rows.
    totalClaims: "---",
    medicareClaims: "---",
    medicaidClaims: "---",
    commercialClaims: "---",
    vaClaims: "---",
    tricareClaims: "---",
    motiveAppropriateness: formatScore(averageByAccessPoint(children, (p) => p.motiveAppropriateness)),
    careJourneyCost: formatScore(averageByAccessPoint(children, (p) => p.careJourneyCost)),
    careJourneyQuality: formatScore(averageByAccessPoint(children, (p) => p.careJourneyQuality)),
  });
  /* oxlint-enable sort-keys */

  const { parents, expansions } = useMemo(() => {
    const parentRows: PractitionerTableRow[] = [];
    const expansionMap = new Map<string, PractitionerTableRow[]>();

    for (const [orgNpi, practitioners] of groups.byOrg) {
      const key = `org:${orgNpi}`;
      const orgName = practitioners.find((p) => p.professionalOrganizationName)?.professionalOrganizationName ?? orgNpi;
      const contractEntityId = practitioners.find((p) => p.professionalOrganizationContractEntityId != null)?.professionalOrganizationContractEntityId;
      const name = (
        <QESButton qesType="link" qesTestId={`ce-practitioners__org-link--${orgNpi}`} className="demibold" onClick={() => props.onOrganizationClick(orgNpi, contractEntityId)}>
          {orgName}
        </QESButton>
      );
      parentRows.push(parentRow(key, name, `NPI (Type 2): ${orgNpi}`, practitioners));
      expansionMap.set(key, practitioners.map((p) => childRow(p, key)));
    }

    if (groups.other.length > 0) {
      parentRows.push(parentRow(OTHER_PRACTITIONERS_KEY, OTHER_PRACTITIONERS_LABEL, "---", groups.other));
      expansionMap.set(OTHER_PRACTITIONERS_KEY, groups.other.map((p) => childRow(p, OTHER_PRACTITIONERS_KEY)));
    }

    return { expansions: expansionMap, parents: parentRows };
  }, [groups, props.onOrganizationClick, props.showPci]);

  // Client-side pagination of the organization (parent) rows — the practitioners are fetched/grouped client-side, so
  // we page the groups here to give the same pagination footer (Previous / Page x of x / page size) as the other tabs.
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  useEffect(() => {
    setPageIndex(1);
  }, [parents.length]);

  const pagedParents = useMemo(() => parents.slice((pageIndex - 1) * pageSize, pageIndex * pageSize), [parents, pageIndex, pageSize]);
  const pagedExpansions = useMemo(() => {
    const map = new Map<string, PractitionerTableRow[]>();
    for (const parent of pagedParents) {
      const parentKey = parent[wafflesKeyColumnName];
      map.set(parentKey, expansions.get(parentKey) ?? []);
    }
    return map;
  }, [pagedParents, expansions]);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageIndex(pageNumber),
    enableBackendPagination: false,
    handlePageSizeChange: (newPageSize: number) => { setPageSize(newPageSize); setPageIndex(1); },
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: pageSize,
    resetToFirstPage: false,
    showPageSizeSelector: true,
    // Client-side paging: WafflesPagination derives its current page (and the Previous/Next disabled state) from skip.
    skip: (pageIndex - 1) * pageSize,
    totalRecords: parents.length,
  };

  return (
    <PageSection className="ce-practitioners-table-section">
      <Waffles<PractitionerTableRow>
        columns={columnConfig}
        data={pagedParents}
        dataExpansions={pagedExpansions}
        dataExpansionsDefaultOpen={false}
        loading={props.loading}
        enablePagination={true}
        paginationProps={paginationProps}
        totalNumberOfRecords={parents.length}
        emptyTableMessage="No employed practitioners found for this contract entity."
        fullWidth={true}
        testId="provider-hub__ce-practitioners-table"
        theme={WafflesThemes.ContractEntityDetails}
        disableStickyHeader={true}
        variant="frozen-border"
      />
    </PageSection>
  );
};

export default ContractEntityEmployedPractitionersTable;
